import React, { useState, useEffect } from "react";
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, Select, MenuItem, FormControl,
  Box, Pagination, Tabs, Tab, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import RefreshIcon from '@mui/icons-material/Refresh';
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SelectWithDialog from "core/components/DialogBox";
import { useUtil } from "mdui/page/UtilProvider";
import { AlertType } from "mdui/page/MsgBox";
import { post } from "mdui/api/HttpReq";

/**
 * Expected API response shape:
 * {
 *   envDetails: [
 *     {
 *       envInfo: <env object>,        // must have envInfo.envId
 *       mgrs: [
 *         {
 *           mgrInfo: <manager object>, // must have mgrInfo.processId
 *           cmd: [ ...menuList ],
 *           flags: { hasStart, hasStop },
 *           processes: [
 *             {
 *               procInfo: <process object>,
 *               cmd: [ ...menuList ],
 *               flags: { hasStart, hasStop, hasWarmboot }
 *             }
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 */

export default function ProcessList() {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProcess, setSelectedProcess] = useState([]);
  const [selectedCmdList, setSelectedCmdList] = useState([]);

  const [cmdArgsDialogOpen, setCmdArgsDialogOpen] = useState(false);
  const [cmdArgsValue, setCmdArgsValue] = useState("");
  const [pendingCmd, setPendingCmd] = useState(null); // { procInfo, mgrInfo, commandText }
  const [loading, setLoading] = useState(true);
  const { openModel } = useUtil();

  // Normalized nested structure from API
  const [envDetails, setEnvDetails] = useState([]); // envDetails[]

  // Tab state
  const [selectedEnvTab, setSelectedEnvTab] = useState(0);
  const [selectedMgrTab, setSelectedMgrTab] = useState({}); // keyed by envId

  // Live process status polling overlay
  const [processStatusMap, setProcessStatusMap] = useState({}); // keyed by procInfo.processName or appID

  // ─── Data Load ───────────────────────────────────────────────────────────────

  const getData = () => {
    setLoading(true);
    async function load() {
      try {
        const val = await post("viewConsoleJobDtls", "list", {});
        const details = val?.envDetails;
        if (Array.isArray(details) && details.length > 0) {
          setEnvDetails(details);
          // Initialize mgr tab map
          const mgrTabInit = {};
          details.forEach(env => {
            mgrTabInit[env.envInfo?.envId] = 0;
          });
          setSelectedMgrTab(mgrTabInit);
          setSelectedEnvTab(0);
        } else {
          openModel({ msg: "no_records", type: AlertType.E });
        }
      } catch (error) {
        console.error("Failed to load job data", error);
        openModel({ msg: "load_error", type: AlertType.E });
      } finally {
        setLoading(false);
      }
    }
    load();
  };

  useEffect(() => {
    getData();
  }, []);

  // ─── Derived Selections ───────────────────────────────────────────────────────

  const currentEnv = envDetails[selectedEnvTab] ?? null;
  const currentEnvId = currentEnv?.envInfo?.envId ?? "";
  const mgrsForCurrentEnv = currentEnv?.mgrs ?? [];

  const currentMgrTabIndex = selectedMgrTab[currentEnvId] ?? 0;
  const currentMgrEntry = mgrsForCurrentEnv[currentMgrTabIndex] ?? null;
  const currentMgrInfo = currentMgrEntry?.mgrInfo ?? null;

  // ─── Tab Handlers ─────────────────────────────────────────────────────────────

  const handleEnvTabChange = (_, newValue) => {
    setSelectedEnvTab(newValue);
    setPage(1);
    setSearchQuery("");
  };

  const handleMgrTabChange = (_, newValue) => {
    setSelectedMgrTab(prev => ({ ...prev, [currentEnvId]: newValue }));
    setPage(1);
    setSearchQuery("");
  };

  // ─── Status Polling ───────────────────────────────────────────────────────────

  const getStts = async () => {
    if (!currentEnvId || !currentMgrInfo?.processId) return;
    try {
      const payload = { env: currentEnvId, mgrName: currentMgrInfo.processId };
      const res = await post("getStts", "list", payload);
      if (Array.isArray(res)) {
        const map = {};
        res.forEach(p => {
          // Index by both appID and processName so either key resolves correctly
          if (p.appID) map[p.appID] = p;
          if (p.processName) map[p.processName] = p;
        });
        setProcessStatusMap(map);
      }
    } catch (err) {
      console.error("Error fetching status", err);
    }
  };

  useEffect(() => {
    if (!currentEnvId || !currentMgrInfo?.processId) return;
    getStts();
    const interval = setInterval(getStts, 30000);
    return () => clearInterval(interval);
  }, [currentEnvId, currentMgrInfo?.processId]);

  // ─── Command Execution ────────────────────────────────────────────────────────

  const executeCommand = async (procInfo, mgrInfo, commandText, param = "") => {
    const payload = {
      proc: procInfo?.processName,
      mgr: mgrInfo?.processId,
      env: currentEnvId,
      param,
      cmd: commandText
    };
    try {
      const resp = await post("execCmd", "list", payload);
      const data = typeof resp === "string" ? JSON.parse(resp) : resp;
      if (data?.result === true) {
        openModel({ msg: `Command ${commandText} executed successfully`, type: AlertType.S });
      } else {
        openModel({ msg: `Command ${commandText} execution failed`, type: AlertType.E });
      }
    } catch (error) {
      console.error("Failed to execute command", error);
      openModel({ msg: "load_error", type: AlertType.E });
    }
  };

  const handleCommand = (procInfo, mgrInfo, commandText, allowArgs = false) => {
    if (allowArgs && allowArgs?.allowArgs === "Y") {
      setPendingCmd({ procInfo, mgrInfo, commandText: allowArgs.cmd });
      setCmdArgsValue("");
      setCmdArgsDialogOpen(true);
      return;
    }
    executeCommand(procInfo, mgrInfo, commandText);
  };

  const handleCmdArgsOk = () => {
    if (pendingCmd) {
      executeCommand(pendingCmd.procInfo, pendingCmd.mgrInfo, pendingCmd.commandText, cmdArgsValue);
    }
    setCmdArgsDialogOpen(false);
    setPendingCmd(null);
    setCmdArgsValue("");
  };

  const handleCmdArgsCancel = () => {
    setCmdArgsDialogOpen(false);
    setPendingCmd(null);
    setCmdArgsValue("");
  };

  // ─── Dialog ───────────────────────────────────────────────────────────────────

  const handleOpenDialog = (procOrMgr, cmdList) => {
    setSelectedProcess(procOrMgr);
    setSelectedCmdList(cmdList || []);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => setOpenDialog(false);

  // ─── Process Table Data ───────────────────────────────────────────────────────

  const rawProcessEntries = currentMgrEntry?.processes ?? [];

  // Debug: warn in dev if manager entry exists but has no processes
  if (process.env.NODE_ENV !== "production" && currentMgrEntry && rawProcessEntries.length === 0) {
    console.warn("ProcessList: currentMgrEntry has no processes[]", currentMgrEntry);
  }

  // Merge live status into each process entry
  // Poll response is keyed by appID; fall back to processName if appID not present on procInfo
  const mergedProcesses = rawProcessEntries.map(entry => {
    const lookupKey = entry.procInfo?.appID ?? entry.procInfo?.processName;
    const liveStatus = lookupKey ? (processStatusMap[lookupKey] ?? {}) : {};
    return {
      ...entry,
      procInfo: {
        ...entry.procInfo,
        // Only spread live fields that are actually defined, so we never
        // overwrite valid procInfo values with undefined from an empty match
        ...(Object.keys(liveStatus).length > 0 ? liveStatus : {})
      }
    };
  });

  const filteredProcesses = !searchQuery.trim()
    ? mergedProcesses
    : mergedProcesses.filter(entry =>
        entry.procInfo?.processName?.toLowerCase().includes(searchQuery.toLowerCase())
      );

  const totalPages = Math.ceil(filteredProcesses.length / rowsPerPage);
  const paginatedProcesses = filteredProcesses.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <Container sx={{ mt: "10px" }} maxWidth="xl">
      <Paper sx={{ padding: "20px", boxShadow: 4, borderRadius: "12px", border: "1px solid #ddd" }}>

        {/* ── Primary Tabs: Environment ── */}
        <Box sx={{ borderBottom: "2px solid #ddd", display: "flex", alignItems: "center" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5, color: "text.secondary", whiteSpace: "nowrap", mr: 1 }}>
            Environment:
          </Typography>
          <Tabs
            value={selectedEnvTab}
            onChange={handleEnvTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {envDetails.map((env, index) => (
              <Tab key={env.envInfo?.envId ?? index} label={env.envInfo?.envId ?? `ENV-${index}`} />
            ))}
          </Tabs>
        </Box>

        {/* ── Secondary Tabs: Manager ── */}
        {mgrsForCurrentEnv.length > 0 && (
          <Box sx={{ mt: 2, borderBottom: "1px solid #eee", display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "text.secondary", whiteSpace: "nowrap", mr: 1 }}>
              Manager:
            </Typography>
            <Tabs
              value={currentMgrTabIndex}
              onChange={handleMgrTabChange}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{ style: { backgroundColor: "#1976d2", height: 3 } }}
              sx={{ "& .MuiTab-root": { fontSize: "0.85rem", minHeight: "36px" }, minHeight: "36px" }}
            >
              {mgrsForCurrentEnv.map((mgrEntry, index) => (
                <Tab
                  key={`${currentEnvId}-${mgrEntry.mgrInfo?.processId ?? index}`}
                  label={mgrEntry.mgrInfo?.processId ?? `MGR-${index}`}
                />
              ))}
            </Tabs>
          </Box>
        )}

        {/* ── Manager Details Card ── */}
        {currentMgrInfo && (
          <Box sx={{ mt: 2, p: 2, background: "#f9f9f9", borderRadius: "8px", border: "1px solid #e0e0e0" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              Manager Details
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
              <Typography variant="body2"><b>Process ID:</b> {currentMgrInfo.processId}</Typography>
              <Typography variant="body2"><b>Process Type:</b> {currentMgrInfo.processType}</Typography>
              <Typography variant="body2"><b>Startup Mode:</b> {currentMgrInfo.startupMode}</Typography>
              <Typography variant="body2"><b>Primary Host Name:</b> {currentMgrInfo.priHostname}</Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                {currentMgrEntry?.flags?.hasStart && (
                  <Tooltip title="Start Manager">
                    <Button
                      variant="contained" color="success" size="small" sx={{ minWidth: "40px" }}
                      onClick={() => handleCommand(currentMgrInfo, currentMgrInfo, "START_PROCESS")}
                    >
                      <PlayArrowIcon />
                    </Button>
                  </Tooltip>
                )}
                {currentMgrEntry?.flags?.hasStop && (
                  <Tooltip title="Stop Manager">
                    <Button
                      variant="contained" color="error" size="small" sx={{ minWidth: "40px" }}
                      onClick={() => handleCommand(currentMgrInfo, currentMgrInfo, "STOP_PROCESS")}
                    >
                      <StopIcon />
                    </Button>
                  </Tooltip>
                )}
                {currentMgrEntry?.cmd?.length > 0 && (
                  <Button
                    variant="contained" color="primary" size="small"
                    onClick={() => handleOpenDialog(currentMgrInfo, currentMgrEntry.cmd)}
                  >
                    Select Command
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Process Table ── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>Process List</Typography>
          <TextField
            size="small" variant="outlined" label="Search"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <Typography variant="body1">Rows per page:</Typography>
          <FormControl size="small">
            <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(1); }}>
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={15}>15</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Process Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Operation Mode</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Start Time</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>PID</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Manager Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Host Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProcesses.map((entry, index) => {
                const proc = entry.procInfo ?? {};
                const flags = entry.flags ?? {};
                const cmdList = entry.cmd ?? [];

                const isAlive = proc.isPhyAlive;
                const statusColor = isAlive === true ? "green" : isAlive === false ? "red" : "yellow";
                const statusLabel = isAlive === true ? "Active" : isAlive === false ? "Down" : "Unknown";

                return (
                  <TableRow key={index}>
                    <TableCell>{proc.processName}</TableCell>
                    <TableCell>{proc.active === "Y" ? "Primary" : "Backup"}</TableCell>
                    <TableCell>
                      <FiberManualRecordIcon style={{ color: statusColor }} />
                      {" "}{statusLabel}
                    </TableCell>
                    <TableCell>{proc.startTime}</TableCell>
                    <TableCell>{proc.pid}</TableCell>
                    <TableCell>{currentMgrInfo?.processId}</TableCell>
                    <TableCell>{proc.hostname ?? currentMgrInfo?.priHostname}</TableCell>
                    <TableCell>
                      {flags.hasStart && (
                        <Tooltip title="Start Process">
                          <Button
                            variant="contained" color="success" size="small"
                            sx={{ minWidth: "40px", mr: 1 }}
                            onClick={() => handleCommand(proc, currentMgrInfo, "START_PROCESS", flags.hasStart)}
                          >
                            <PlayArrowIcon />
                          </Button>
                        </Tooltip>
                      )}
                      {flags.hasStop && (
                        <Tooltip title="Stop Process">
                          <Button
                            variant="contained" color="error" size="small"
                            sx={{ minWidth: "40px", mr: 1 }}
                            onClick={() => handleCommand(proc, currentMgrInfo, "STOP_PROCESS", flags.hasStop)}
                          >
                            <StopIcon />
                          </Button>
                        </Tooltip>
                      )}
                      {flags.hasWarmboot && (
                        <Tooltip title="Warmboot Process">
                          <Button
                            variant="contained" color="warning" size="small"
                            sx={{ minWidth: "40px", mr: 1 }}
                            onClick={() => handleCommand(proc, currentMgrInfo, "WARMBOOT", flags.hasWarmboot)}
                          >
                            <RefreshIcon />
                          </Button>
                        </Tooltip>
                      )}
                      {cmdList.length > 0 && (
                        <Button
                          variant="contained" color="primary" size="small"
                          onClick={() => handleOpenDialog(proc, cmdList)}
                        >
                          Select Command
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" justifyContent="right" mt={3}>
          <Pagination
            count={totalPages} page={page} onChange={(_, v) => setPage(v)}
            color="primary" showFirstButton showLastButton
          />
        </Box>
      </Paper>

      <SelectWithDialog
        open={openDialog}
        onClose={handleCloseDialog}
        cmdList={selectedCmdList}
        process={selectedProcess}
        envId={currentEnvId}
        cmdOpts={{}}
      />

      {/* Command Arguments Dialog */}
      <Dialog open={cmdArgsDialogOpen} onClose={handleCmdArgsCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          Enter Command Arguments for [{pendingCmd?.commandText}]
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            [{pendingCmd?.commandText}] supports input parameters.
          </Typography>
          <Typography gutterBottom>
            Do you wish to enter any parameters?
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            You may leave blank and press 'OK' for default parameters to be sent.
            Pressing 'Cancel' will prevent [{pendingCmd?.commandText}] from being sent.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Example:</strong> key1='Value1';key2='Value2';key3='Value3';
          </Typography>
          <TextField
            fullWidth
            value={cmdArgsValue}
            onChange={(e) => setCmdArgsValue(e.target.value)}
            placeholder="Enter parameters here..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCmdArgsCancel} variant="contained" color="info">Cancel</Button>
          <Button onClick={handleCmdArgsOk} variant="contained" color="primary">OK</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}